/**
 * SuggestedActions component.
 *
 * Renders dynamic context-aware action pills below the latest bot message.
 * Pills are styled per entity branding and support sparkle indicators for
 * highlighted/recommended actions. The free-text input is ALWAYS visible
 * alongside pills — pills supplement conversation, they never replace it.
 *
 * Backend sends: { type: "suggested_actions", actions: [{label, action, confidence, sparkle}] }
 * Only actions above the confidence threshold are displayed (default 0.6).
 * Clicking a pill emits the action and removes all pills.
 * Keyboard navigation: arrow keys move focus between pills, Enter/Space selects.
 */

import type {
  ResolvedWidgetConfig,
  SuggestedAction,
  SuggestedActionAnalyticsEvent,
  SuggestedActionsDisplay,
} from "../types";
import { EXTERNAL_LINK_SVG } from "./icons";

/** Default confidence threshold — only show pills scoring above this */
const DEFAULT_CONFIDENCE_THRESHOLD = 0.6;

/** Maximum number of pills to display at once */
const MAX_PILLS = 5;

/**
 * GH #560 (S4b) — doctor-selection pills. The backend's discovery turn
 * (`specs/subspecialty-matching.md` §5a) sends actions whose payload is
 * the machine token `doctor_select:<DoctorGivenID>`; the reserved
 * `doctor_select:back` token is the detail card's list-replay action.
 * Doctor pills render as a two-line branded card (name + ≤4-word
 * capability tag) in a vertical list — NEVER paragraphs (plan §4.2).
 *
 * The display cap for doctor pills is the SERVER's admin-configured
 * picker_limit (the backend already capped the list); the widget keeps
 * a defensive ceiling only so a misbehaving payload can't flood the
 * pane. The ceiling sits well above any realistic picker_limit so it is
 * a FLOOD GUARD, not a product cap — it must never silently clip a
 * server-capped list (review LOW-1, `observability.md` no-silent-caps).
 */
const DOCTOR_SELECT_PREFIX = "doctor_select:";
const DOCTOR_BACK_ACTION = "doctor_select:back";
const MAX_DOCTOR_PILLS = 25;

/** Em-dash separator the backend uses in doctor labels: "Name — Tag". */
const DOCTOR_LABEL_SEPARATOR = " — ";

function isDoctorSelectAction(action: SuggestedAction): boolean {
  return (
    action.action.startsWith(DOCTOR_SELECT_PREFIX) &&
    action.action !== DOCTOR_BACK_ACTION
  );
}

export interface SuggestedActionsOptions {
  config: ResolvedWidgetConfig;
  /** Confidence threshold (0.0 - 1.0). Actions below this are hidden. */
  confidenceThreshold?: number;
  /** Called when a pill is clicked. Receives the action payload string. */
  onAction: (action: string, label: string) => void;
  /** Called when an analytics event should be recorded */
  onAnalytics?: (event: SuggestedActionAnalyticsEvent) => void;
  /** Display style for this suggestion group. */
  display?: SuggestedActionsDisplay;
}

export class SuggestedActions {
  private readonly element: HTMLElement;
  private readonly config: ResolvedWidgetConfig;
  private readonly confidenceThreshold: number;
  private readonly onAction: (action: string, label: string) => void;
  private readonly onAnalytics?: (event: SuggestedActionAnalyticsEvent) => void;
  private readonly display: SuggestedActionsDisplay;
  private pills: HTMLButtonElement[] = [];
  private dismissed = false;

  constructor(options: SuggestedActionsOptions) {
    this.config = options.config;
    this.confidenceThreshold =
      options.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD;
    this.onAction = options.onAction;
    this.onAnalytics = options.onAnalytics;
    this.display = options.display ?? "pills";

    this.element = document.createElement("div");
    this.element.className = "hana-suggested-actions";
    this.element.setAttribute("role", "group");
    this.element.setAttribute("aria-label", "Suggested actions");
  }

  /**
   * Get the root container element for mounting into the message list.
   */
  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Render pills from a backend payload.
   * Filters by confidence threshold, caps at MAX_PILLS, and applies
   * entity branding. Returns the number of pills actually rendered.
   */
  renderActions(actions: SuggestedAction[]): number {
    // Clear any existing pills
    this.clear();
    this.dismissed = false;

    // GH #560 (S4b): a group containing doctor-selection pills is a
    // DOCTOR LIST — the server already capped it at the admin's
    // picker_limit, so the generic MAX_PILLS cap must not clip it
    // (defensive ceiling only). A group containing the reserved back
    // token is a DETAIL CARD action set — its "See available slots"
    // sibling gets primary styling below.
    const hasDoctorPills = actions.some(isDoctorSelectAction);
    const isDetailCard = actions.some((a) => a.action === DOCTOR_BACK_ACTION);

    // Filter by confidence threshold and cap
    const eligible = actions
      .filter((a) => a.confidence >= this.confidenceThreshold)
      .slice(0, hasDoctorPills ? MAX_DOCTOR_PILLS : MAX_PILLS);

    if (eligible.length === 0) {
      // Empty state: no pills, no space taken
      return 0;
    }

    // Add entrance animation class
    this.element.classList.add("hana-suggested-actions--entering");
    if (this.display === "list" || eligible.length > 4 || hasDoctorPills) {
      this.element.classList.add("hana-suggested-actions--list");
    }
    if (hasDoctorPills) {
      this.element.classList.add("hana-suggested-actions--doctors");
      this.element.setAttribute("aria-label", "Doctor options");
    }
    if (isDetailCard) {
      this.element.classList.add("hana-suggested-actions--detail");
    }

    eligible.forEach((action, index) => {
      const pill = this.createPill(action, index, { isDetailCard });
      this.pills.push(pill);
      this.element.appendChild(pill);
    });

    // Emit analytics: pills displayed
    this.emitDisplayedAnalytics(eligible);

    // Remove entrance class after animation completes
    const handleTransitionEnd = (): void => {
      this.element.classList.remove("hana-suggested-actions--entering");
      this.element.removeEventListener("animationend", handleTransitionEnd);
    };
    this.element.addEventListener("animationend", handleTransitionEnd);

    return eligible.length;
  }

  /**
   * Remove all pills and collapse the container (no empty space).
   */
  dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    this.clear();
  }

  /**
   * Whether the pills have been dismissed (clicked or externally dismissed).
   */
  isDismissed(): boolean {
    return this.dismissed;
  }

  private clear(): void {
    this.pills = [];
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild);
    }
    this.element.classList.remove("hana-suggested-actions--entering");
    this.element.classList.remove("hana-suggested-actions--list");
    this.element.classList.remove("hana-suggested-actions--doctors");
    this.element.classList.remove("hana-suggested-actions--detail");
    this.element.setAttribute("aria-label", "Suggested actions");
  }

  private createPill(
    action: SuggestedAction,
    index: number,
    group: { isDetailCard: boolean } = { isDetailCard: false },
  ): HTMLButtonElement {
    const pill = document.createElement("button");
    pill.className = "hana-action-btn hana-suggested-pill";
    pill.setAttribute("type", "button");
    pill.setAttribute("role", "option");
    pill.setAttribute("aria-label", action.label);
    pill.setAttribute("data-action", action.action);
    pill.setAttribute("data-index", String(index));

    // Apply sparkle class for highlighted actions
    if (action.sparkle) {
      pill.classList.add("hana-sparkle");
      pill.classList.add("hana-action-btn--primary");
    }

    if (isDoctorSelectAction(action)) {
      // GH #560 (S4b) — doctor pill: two-line card (name strong, the
      // ≤4-word capability tag muted beneath). Tag-only invariant:
      // ONLY label-derived name + metadata.capability_tag ever render
      // here — paragraphs/summaries belong to the detail card.
      // All text lands via textContent (auto-escaped; security.md
      // § Output Encoding — the tag is admin/LLM-authored).
      pill.classList.add("hana-doctor-pill");
      const { name, tag } = this.splitDoctorLabel(action);
      const nameSpan = document.createElement("span");
      nameSpan.className = "hana-doctor-pill__name";
      nameSpan.textContent = name;
      pill.appendChild(nameSpan);
      if (tag) {
        const tagSpan = document.createElement("span");
        tagSpan.className = "hana-doctor-pill__tag";
        tagSpan.textContent = tag;
        pill.appendChild(tagSpan);
        pill.setAttribute("aria-label", `${name} — ${tag}. View details`);
      } else {
        pill.setAttribute("aria-label", `${name}. View details`);
      }
    } else if (group.isDetailCard && action.action !== DOCTOR_BACK_ACTION) {
      // Detail-card action set: the booking entry ("See available
      // slots") is the primary affordance; "Back to doctor list"
      // stays secondary.
      pill.classList.add("hana-action-btn--primary");
      const labelSpan = document.createElement("span");
      labelSpan.className = "hana-suggested-pill__label";
      labelSpan.textContent = action.label;
      pill.appendChild(labelSpan);
    } else {
      // Pill text with truncation via CSS
      const labelSpan = document.createElement("span");
      labelSpan.className = "hana-suggested-pill__label";
      labelSpan.textContent = action.label;
      pill.appendChild(labelSpan);
    }

    // External link icon for booking/sparkle actions
    if (action.sparkle || action.action.includes("booking_link")) {
      const iconSpan = document.createElement("span");
      iconSpan.className = "hana-suggested-pill__ext-icon";
      iconSpan.innerHTML = EXTERNAL_LINK_SVG;
      pill.appendChild(iconSpan);
    }

    // Click handler: emit action, dismiss pills
    pill.addEventListener("click", () => {
      this.handlePillClick(action);
    });

    // Keyboard navigation
    pill.addEventListener("keydown", (e: KeyboardEvent) => {
      this.handlePillKeydown(e, index);
    });

    return pill;
  }

  /**
   * Split a doctor pill's backend label ("Dr Name — Tag") into its two
   * display lines. The capability tag prefers the §4.2
   * `metadata.capability_tag`; the name is the label with the
   * known " — <tag>" suffix stripped when it matches. Falls back to
   * rendering the whole label as the name (tag-less doctors render
   * name-only per the degraded-state contract).
   */
  private splitDoctorLabel(action: SuggestedAction): {
    name: string;
    tag: string;
  } {
    const tag = action.metadata?.capability_tag?.trim() ?? "";
    if (tag) {
      const suffix = `${DOCTOR_LABEL_SEPARATOR}${tag}`;
      if (action.label.endsWith(suffix)) {
        return {
          name: action.label.slice(0, action.label.length - suffix.length),
          tag,
        };
      }
      return { name: action.label, tag };
    }
    // No metadata tag — try the label's own "Name — Tag" shape so a
    // metadata-less payload still renders two lines.
    const sepIndex = action.label.lastIndexOf(DOCTOR_LABEL_SEPARATOR);
    if (sepIndex > 0) {
      return {
        name: action.label.slice(0, sepIndex),
        tag: action.label.slice(sepIndex + DOCTOR_LABEL_SEPARATOR.length),
      };
    }
    return { name: action.label, tag: "" };
  }

  private handlePillClick(action: SuggestedAction): void {
    if (this.dismissed) return;

    // Emit analytics: pill clicked
    this.emitClickedAnalytics(action);

    // Emit the action
    this.onAction(action.action, action.label);

    // Dismiss all pills
    this.dismiss();
  }

  private handlePillKeydown(e: KeyboardEvent, currentIndex: number): void {
    let targetIndex: number | null = null;

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        targetIndex =
          currentIndex < this.pills.length - 1 ? currentIndex + 1 : 0;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        targetIndex =
          currentIndex > 0 ? currentIndex - 1 : this.pills.length - 1;
        break;
      case "Home":
        e.preventDefault();
        targetIndex = 0;
        break;
      case "End":
        e.preventDefault();
        targetIndex = this.pills.length - 1;
        break;
    }

    if (targetIndex !== null && this.pills[targetIndex]) {
      this.pills[targetIndex].focus();
    }
  }

  private emitDisplayedAnalytics(actions: SuggestedAction[]): void {
    if (!this.onAnalytics) return;

    for (const action of actions) {
      this.onAnalytics({
        type: "suggested_action_displayed",
        entityId: this.config.entityId,
        timestamp: new Date().toISOString(),
        label: action.label,
        displayedCount: actions.length,
      });
    }
  }

  private emitClickedAnalytics(action: SuggestedAction): void {
    if (!this.onAnalytics) return;

    this.onAnalytics({
      type: "suggested_action_clicked",
      entityId: this.config.entityId,
      timestamp: new Date().toISOString(),
      label: action.label,
      action: action.action,
    });
  }
}
